<script setup>
import { onMounted } from 'vue'

// Runtime E-E-A-T Schema Injection targeting Google's structured data engine
onMounted(() => {
  const scriptId = 'eeat-founder-schema'

  // Prevent duplicate script injections during hot reloads or component changes
  if (document.getElementById(scriptId)) return

  const script = document.createElement('script')
  script.id = scriptId
  script.type = 'application/ld+json'

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": "https://www.assetnivesh.com/#founder",
        "name": "Vivek Gupta",
        "jobTitle": "Founder & Investment Advisor",
        "url": "https://www.assetnivesh.com",
        "image": "https://www.assetnivesh.com/_nuxt/img/MyPhoto_Lib.jpg", // Dynamically maps to your local image asset path
        "knowsAbout": ["Wealth Management", "Quantitative Finance", "Technical Analysis", "Portfolio Analytics"],
        "alumniOf": [
          {
            "@type": "EducationalOrganization",
            "name": "Indian Institute of Technology, Delhi",
            "alternateName": "IIT Delhi",
            "award": "M.Tech"
          }
        ],
        "hasCredential": [
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Charter",
            "name": "CFA® Charterholder",
            "recognizedBy": {
              "@type": "Organization",
              "name": "CFA Institute"
            }
          },
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Regulatory License",
            "name": "SEBI Registered Investment Adviser",
            "credentialNumber": "INA000020554",
            "recognizedBy": {
              "@type": "GovernmentOrganization",
              "name": "Securities and Exchange Board of India",
              "alternateName": "SEBI"
            },
            "url": "https://www.sebi.gov.in/sebiweb/other/OtherAction.do?doRecognisedFpi=yes&intmId=13"
          }
        ],
        "worksFor": {
          "@id": "https://www.assetnivesh.com/#organization"
        }
      },
      {
        "@type": "FinancialService",
        "@id": "https://www.assetnivesh.com/#organization",
        "name": "AssetNivesh",
        "legalName": "Joie Capital Research Private Limited",
        "alternateName": "AssetNivesh Portal",
        "url": "https://www.assetnivesh.com",
        "logo": "https://www.assetnivesh.com/assets/images/logo.png",
        "telephone": "+919958836060",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "65, RPS, Sheikh Sarai, Phase I",
          "addressLocality": "New Delhi",
          "postalCode": "110017",
          "addressCountry": "IN"
        },
        "serviceType": ["Investment Advisory", "Portfolio Analysis", "Model Portfolios"],
        "feesAndCommissionsSpecification": "Fee-only direct advisory model. No distribution commissions received."
      }
    ]
  }

  script.text = JSON.stringify(schemaData)
  document.head.appendChild(script)
})
</script>

<template>
  <section class="founder-section">
    <div class="founder-container">
      <div class="founder-header">
        <div class="section-label">A Message from the Founder</div>
        <h2 class="founder-title">Bringing Clarity to Your Investment Journey</h2>
      </div>

      <div class="founder-content">
        <div class="founder-visual">
          <div class="photo-frame">
            <img
              src="@/assets/images/MyPhoto_Lib.jpg"
              alt="Vivek Gupta - Founder, Asset Nivesh"
              class="founder-photo"
              loading="lazy"
            />
            <div class="photo-gradient"></div>
          </div>
          <div class="founder-name-card">
            <h3 class="founder-name">Vivek Gupta</h3>
            <p class="founder-role">Founder & Investment Advisor</p>
            <div class="founder-credentials">
              <span class="credential-item">CFA® Charterholder</span>
              <span class="credential-item">IIT Delhi (M.Tech)</span>
              <span class="credential-item">25+ Years Experience</span>
            </div>
          </div>
        </div>

        <div class="founder-text">
          <div class="message-block opening">
            <p class="narrative-text emphasis">
              Most investors don't struggle because markets are difficult.<br/>
              They struggle because decisions feel confusing, inconsistent, and overwhelming.
            </p>
          </div>

          <div class="message-block">
            <p class="narrative-text">
              Over the years, I've witnessed this closely—not just professionally, but personally. Growing up, I saw people around me, including my own parents, work hard and save diligently, yet still struggle to build meaningful wealth. Without assured pensions, the pressure to "get investments right" becomes intense.
            </p>
            <p class="narrative-text">
              I've seen families lock large savings into real estate—only to realize how difficult liquidation becomes when cash is needed. I've seen people invest in insurance policies without understanding they may be sub-optimal as wealth-building tools. These decisions aren't made from ignorance, but from trust, habit, and lack of clear guidance.
            </p>
            <p class="narrative-text strong">
              That's the real problem—not lack of products, but lack of clarity.
            </p>
          </div>

          <div class="message-block highlight-block">
            <h3 class="block-title">My Promise to You</h3>
            <ul class="promise-list">
              <li>I will help you stay disciplined, especially when markets test your patience</li>
              <li>I will bring clarity to every decision, so you always know <em>why</em> you're investing</li>
            </ul>
            <p class="closing-tagline">
              It's about building wealth systematically, transparently, and with confidence.
            </p>
          </div>

          <div class="message-block">
            <h3 class="block-title">Why I Built AssetNivesh</h3>
            <p class="narrative-text">
              Over 25 years building mission-critical financial systems at IBM, TCS, and Cognizant, I repeatedly saw a gap between complex financial data and actionable insights.
            </p>
            <p class="narrative-text">
              The same complexity institutions manage with advanced systems continues to confuse individual investors. I founded AssetNivesh to bridge this gap—bringing institutional-grade discipline in a way that is simple, explainable, and aligned to personal goals.
            </p>
          </div>
          <div class="message-block closing-block">
            <p class="narrative-text">
              If you're someone who wants to build wealth with structure, avoid unnecessary risks, and stay on track toward your financial goals—AssetNivesh is built for you.
            </p>
            <p class="narrative-text emphasis">
              I look forward to being a part of your journey.
            </p>
            <div class="signature">
              <p class="signature-name">— Vivek Gupta</p>
              <p class="signature-role">Founder & Investment Advisor</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.founder-section {
  padding: 48px 24px;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 50%, #f0f9ff 100%);
  position: relative;
  overflow: hidden;
}

.founder-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, #e5e7eb 20%, #e5e7eb 80%, transparent);
}

.founder-container {
  max-width: 1240px;
  margin: 0 auto;
}

.founder-header {
  text-align: center;
  margin-bottom: 32px;
}

.section-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.section-label::before {
  content: '';
  width: 40px;
  height: 2px;
  background: linear-gradient(to right, #3b82f6, transparent);
}

.section-label::after {
  content: '';
  width: 40px;
  height: 2px;
  background: linear-gradient(to left, #3b82f6, transparent);
}

.founder-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
  margin: 0;
  letter-spacing: -0.02em;
}

.founder-content {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 40px;
  align-items: start;
}

.founder-visual {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 100px;
}

.photo-frame {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 66px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  aspect-ratio: 3/4;
}

.founder-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.photo-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to top, rgba(31, 41, 55, 0.8) 0%, transparent 100%);
  pointer-events: none;
}

.founder-name-card {
  background: white;
  padding: 18px;
  border-radius: 13px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  text-align: center;
}

.founder-name {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.founder-role {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 12px 0;
  font-weight: 500;
}

.founder-credentials {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.credential-item {
  font-size: 0.8125rem;
  color: #1e40af;
  background-color: #dbeafe;
  font-weight: 600;
  display: block;
}

.founder-text {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.message-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-block.opening {
  margin-bottom: 4px;
}

.block-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.narrative-text {
  font-size: 0.9375rem;
  color: #4b5563;
  line-height: 1.65;
  margin: 0;
}

.narrative-text.emphasis {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.55;
}

.narrative-text.strong {
  font-weight: 600;
  color: #1f2937;
  font-size: 1rem;
}

.narrative-text.center-text {
  text-align: center;
  margin-top: 8px;
}

.narrative-text.platform-tagline {
  font-style: italic;
  color: #1f2937;
  margin-top: 8px;
}

.narrative-text em {
  font-style: italic;
  color: #3b82f6;
  font-weight: 600;
}

.highlight-block {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  padding: 16px 20px;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.highlight-block .block-title {
  color: #1e40af;
  margin: 0 0 10px 0;
  font-size: 1.125rem;
}

.promise-list {
  list-style: none;
  padding: 0;
  margin: 0 0 10px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.promise-list li {
  font-size: 0.9375rem;
  color: #1e40af;
  line-height: 1.5;
  padding-left: 22px;
  position: relative;
}

.promise-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #3b82f6;
  font-weight: 700;
  font-size: 0.9375rem;
}

.closing-tagline {
  font-size: 0.9375rem;
  color: #1f2937;
  font-weight: 500;
  font-style: italic;
  margin: 0;
  text-align: center;
  padding-top: 6px;
  border-top: 1px solid rgba(59, 130, 246, 0.2);
}

.belief-block {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px 28px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.belief-icon {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
}

.belief-icon i {
  font-size: 1.375rem;
  color: white;
}

.belief-text {
  font-size: 1.0625rem;
  color: white;
  font-weight: 600;
  line-height: 1.6;
  margin: 0;
  font-style: italic;
}

.closing-block {
  padding-top: 0;
}

.signature {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #e5e7eb;
}

.signature-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
  font-style: italic;
}

.signature-role {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  font-weight: 500;
}

@media (max-width: 1024px) {
  .founder-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .founder-visual {
    max-width: 280px;
    margin: 0 auto;
    position: static;
  }

  .founder-title {
    font-size: 2rem;
  }

  .block-title {
    font-size: 1.125rem;
  }
}

@media (max-width: 768px) {
  .founder-section {
    padding: 40px 20px;
  }

  .founder-header {
    margin-bottom: 24px;
  }

  .founder-title {
    font-size: 1.75rem;
  }

  .founder-content {
    gap: 28px;
  }

  .founder-visual {
    max-width: 260px;
  }

  .block-title {
    font-size: 1.0625rem;
  }

  .narrative-text {
    font-size: 0.9375rem;
  }

  .narrative-text.emphasis {
    font-size: 0.9375rem;
  }

  .highlight-block {
    padding: 14px 18px;
  }

  .highlight-block .block-title {
    font-size: 1rem;
    margin: 0 0 8px 0;
  }

  .promise-list li {
    font-size: 0.875rem;
  }

  .closing-tagline {
    font-size: 0.875rem;
    padding-top: 5px;
  }

  .founder-text {
    gap: 20px;
  }

  .message-block {
    gap: 10px;
  }

  .belief-block {
    flex-direction: column;
    text-align: center;
    padding: 20px 18px;
  }

  .belief-text {
    font-size: 1rem;
  }
}

@media (max-width: 640px) {
  .founder-section {
    padding: 32px 16px;
  }

  .founder-header {
    margin-bottom: 20px;
  }

  .section-label {
    font-size: 0.75rem;
  }

  .section-label::before,
  .section-label::after {
    width: 20px;
  }

  .founder-title {
    font-size: 1.5rem;
  }

  .founder-content {
    gap: 24px;
  }

  .founder-visual {
    max-width: 100%;
  }

  .photo-frame {
    max-height: 350px;
  }

  .founder-name {
    font-size: 1.25rem;
  }

  .founder-role {
    font-size: 0.8125rem;
  }

  .founder-credentials {
    gap: 3px;
    padding-top: 10px;
  }

  .credential-item {
    font-size: 0.75rem;
  }

  .block-title {
    font-size: 1rem;
    margin: 0 0 6px 0;
  }

  .narrative-text,
  .narrative-text.emphasis {
    font-size: 0.875rem;
    line-height: 1.6;
  }

  .narrative-text.strong {
    font-size: 0.9375rem;
  }

  .highlight-block {
    padding: 12px 16px;
  }

  .highlight-block .block-title {
    font-size: 0.9375rem;
    margin: 0 0 6px 0;
  }

  .promise-list {
    margin: 0 0 8px 0;
    gap: 5px;
  }

  .promise-list li {
    font-size: 0.8125rem;
    padding-left: 20px;
    line-height: 1.4;
  }

  .promise-list li::before {
    font-size: 0.8125rem;
  }

  .closing-tagline {
    font-size: 0.8125rem;
    padding-top: 5px;
  }

  .belief-block {
    padding: 18px 16px;
  }

  .belief-icon {
    width: 40px;
    height: 40px;
  }

  .belief-icon i {
    font-size: 1rem;
  }

  .belief-text {
    font-size: 0.875rem;
  }

  .founder-text {
    gap: 18px;
  }

  .message-block {
    gap: 10px;
  }

  .message-block.opening {
    margin-bottom: 2px;
  }

  .signature {
    margin-top: 12px;
    padding-top: 12px;
  }

  .signature-name {
    font-size: 1rem;
  }

  .signature-role {
    font-size: 0.8125rem;
  }
}

@media (max-width: 480px) {
  .founder-section {
    padding: 24px 16px;
  }

  .founder-title {
    font-size: 1.375rem;
  }

  .founder-content {
    gap: 20px;
  }

  .founder-visual {
    gap: 12px;
  }

  .photo-frame {
    max-height: 320px;
  }

  .founder-name-card {
    padding: 14px;
  }

  .founder-text {
    gap: 16px;
  }

  .block-title {
    font-size: 0.9375rem;
  }

  .narrative-text,
  .narrative-text.emphasis,
  .narrative-text.strong {
    font-size: 0.8125rem;
  }
}
</style>
